import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";

const STEEL_IMG =
    "https://images.unsplash.com/photo-1557143930-d4e7a86a194f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const FOUNDER_IMG =
    "https://images.unsplash.com/photo-1622109912940-2bddde35274d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function About() {
    const { t } = useLang();
    return (
        <main data-testid="page-about" className="bg-white">
            <SEO pathKey="about" path="/about" />
            <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 blueprint-bg">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-8">
                        <Reveal>
                            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-steel-600">
                                {t.about.kicker}
                            </div>
                            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter text-navy leading-[0.92]">
                                {t.about.h1}
                            </h1>
                        </Reveal>
                    </div>
                    <Reveal delay={120} className="lg:col-span-4">
                        <p className="text-steel-600 leading-relaxed">{t.about.intro}</p>
                    </Reveal>
                </div>
            </section>

            <section className="relative h-[40vh] lg:h-[55vh] overflow-hidden">
                <img src={STEEL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
                <div className="absolute inset-0 bg-navy/30" />
                <div className="absolute bottom-6 left-6 lg:bottom-10 lg:left-10 font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/80">
                    OTHRYX · 01 / Field Operations
                </div>
            </section>

            <section className="py-24 lg:py-32">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 border-t border-s border-steel-100">
                    <Reveal className="p-8 lg:p-12 border-e border-b border-steel-100">
                        <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-navy">
                            {t.about.missionTitle}
                        </div>
                        <p className="mt-6 text-xl text-navy leading-snug font-display tracking-tight">
                            {t.about.missionBody}
                        </p>
                    </Reveal>
                    <Reveal delay={120} className="p-8 lg:p-12 border-e border-b border-steel-100 bg-steel-50">
                        <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-navy">
                            {t.about.modelTitle}
                        </div>
                        <p className="mt-6 text-xl text-navy leading-snug font-display tracking-tight">
                            {t.about.modelBody}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-navy text-white py-24 lg:py-32 relative overflow-hidden grain">
                <div className="absolute inset-0 blueprint-bg opacity-10" />
                <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <Reveal className="lg:col-span-4">
                        <div className="aspect-[4/5] overflow-hidden border border-white/10">
                            <img src={FOUNDER_IMG} alt={t.about.founderName} className="w-full h-full object-cover grayscale" />
                        </div>
                    </Reveal>
                    <div className="lg:col-span-8 lg:ps-6">
                        <Reveal delay={100}>
                            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-steel-400">
                                {t.about.founderOverline}
                            </div>
                            <h2 className="font-display mt-4 text-4xl lg:text-6xl uppercase tracking-tighter leading-[0.95]">
                                {t.about.founderName}
                            </h2>
                            <p className="mt-8 max-w-2xl text-steel-200 text-lg leading-relaxed">
                                {t.about.founderBio}
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="py-24 lg:py-32">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                    <Reveal>
                        <h2 className="font-display text-4xl lg:text-5xl uppercase tracking-tighter text-navy leading-[0.95]">
                            {t.about.pillars.title}
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-s border-steel-100">
                        {t.about.pillars.items.map((p, i) => (
                            <Reveal
                                key={p.n}
                                delay={i * 80}
                                className="p-8 border-e border-b border-steel-100 bg-white"
                            >
                                <div className="font-display text-4xl text-steel-200">{p.n}</div>
                                <div className="mt-6 font-display text-2xl uppercase tracking-tight text-navy">
                                    {p.t}
                                </div>
                                <p className="mt-3 text-steel-600 leading-relaxed text-sm">{p.b}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-steel-50 border-t border-steel-100 py-20">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="font-display text-3xl lg:text-4xl text-navy uppercase tracking-tighter leading-tight">
                        {t.home.cta.title}
                    </div>
                    <Link
                        to="/contact"
                        data-testid="about-cta"
                        className="inline-flex items-center gap-3 bg-navy text-white px-7 py-4 font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-navy-light transition-colors"
                    >
                        {t.common.partner}
                        <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                    </Link>
                </div>
            </section>
        </main>
    );
}
